import { setMode, useBEOController } from "@context/BeoContext";
import { PrimeReactContext } from "primereact/api";
import { useCallback, useContext, useState } from "react";
/**
 * Custom hook for managing and switching application themes.
 * It integrates with the global BEO context for theme state,
 * PrimeReact's theme switching functionality, and applies dark mode
 * classes to the document element for consistent theming.
 *
 * @hook
 * @returns {object} An object containing the `applyTheme` function.
 * @property {function(string): void} applyTheme - A memoized function to apply a new theme.
 * @author niyas.shajahan
 * @since 2025-06-19
 */
export var useThemeSwitcher = function () {
    /**
     * Retrieves the global BEO context and dispatch function.
     * @type {Array<object, function>}
     */
    var _a = useBEOController(), context = _a[0], dispatch = _a[1];
    /**
     * Accesses PrimeReact's `changeTheme` function from its context.
     * @type {function(string, string): void}
     */
    var changeTheme = useContext(PrimeReactContext).changeTheme;
    /**
     * Local state to keep track of the currently applied theme,
     * initialized from the global BEO context's theme.
     * @type {[string, function]}
     */
    var _b = useState(context.theme), localTheme = _b[0], setLocalTheme = _b[1];
    /**
     * Dynamically changes the current PrimeReact theme by manipulating the DOM.
     * It removes any existing theme stylesheet and appends a new one based on the `themeName`.
     * It also handles adding/removing the 'dark' class to the `documentElement` and
     * storing the theme preference in `localStorage`.
     *
     * @private
     * @param {string} themeName - The name of the theme to apply (e.g., "lara-light-blue", "saga-dark").
     */
    function changeCurrentTheme(themeName) {
        var link = document.createElement("link");
        link.id = "primereact-theme";
        link.rel = "stylesheet";
        link.href = "/themes/".concat(themeName, "/theme.css"); // Ensure theme path is correct
        document.head.appendChild(link);
        if (themeName.includes("dark") ||
            themeName.includes("arya") ||
            themeName.includes("vela")) {
            document.documentElement.classList.add("dark");
            localStorage.setItem("theme", "dark");
        }
        else {
            document.documentElement.classList.remove("dark");
            localStorage.setItem("theme", "light");
        }
    }
    /**
     * Memoized callback function to apply a new theme across the application.
     * It updates the local theme state, dispatches the theme change to the global BEO context,
     * and calls `changeCurrentTheme` to dynamically load the stylesheet.
     * It prevents unnecessary re-applications if the new theme is the same as the current one.
     *
     * @param {string} newTheme - The name of the theme to apply.
     */
    var applyTheme = useCallback(function (newTheme) {
        if (!changeTheme || localTheme === newTheme && newTheme !== "lara-light-blue")
            return;
        setLocalTheme(newTheme);
        setMode(dispatch, newTheme);
        changeCurrentTheme(newTheme);
    }, [changeTheme, dispatch, localTheme]);
    return { applyTheme: applyTheme };
};
